import { HasOnlyExactlyThisProperty, UnknownObject } from '@monofront/types';
import React from 'react';

type ProviderTuple<TComponent extends React.ComponentType> = [
  TComponent,
  React.ComponentProps<TComponent>,
];

/**
 * Enabled declaring an array of providers with their props, instead of nesting them which
 * becomes less and less readable overtime, especially when dealing with git conflicts.
 *
 * @example
 * ```tsx
 * <ComposeProviders
 *  providers={[
 *   c(ProviderA, { propA: 'value' }),
 *   c(ProviderB),
 *   c(ProviderC, { propC: 'value' }),
 * ]}
 * >
 *   {children}
 * </ComposeProviders>
 * ```
 * Becomes
 * ```tsx
 * <ProviderA propA="value">
 *   <ProviderB>
 *     <ProviderC propC="value">
 *       {children}
 *     </ProviderC>
 *   </ProviderB>
 * </ProviderA>
 * ```
 */
export const ComposeProviders = <
  TProviders extends Array<ProviderTuple<React.ComponentType<UnknownObject>>>,
>({
  providers,
  children,
}: {
  providers: TProviders;
  children: React.ReactNode;
}) => {
  return (
    <>
      {providers.reduceRight((acc, [Provider, props]) => {
        return <Provider {...props}>{acc}</Provider>;
      }, children)}
    </>
  );
};

type cProps<TProps> = true extends HasOnlyExactlyThisProperty<
  TProps,
  'children'
>
  ? [
      Provider: React.ComponentType<React.PropsWithChildren<TProps>>,
      // This enables the user to not have to pass an empty object when using `c`
      // with a provider that doesn't require props.
      // Caveat: provider with props that are optional will require the user to pass
      // an empty object.
      props?: never,
    ]
  : [
      Provider: React.ComponentType<React.PropsWithChildren<TProps>>,
      // We omit children, because they will be handled by `ComposeProviders`.
      props: Omit<TProps, 'children'>,
    ];

/**
 * Utility function for `ComposeProviders` to declare a provider with its props with type safety.
 */
// Function arguments can be represented as a tuple, which is what we get when we use
// `Parameters<typeof myFunction>`. We can then use the `...` operator to destructure the
// tuple into individual arguments, the inverse operation of `Parameters`.
// We need to be able to type the second arguments, `props`, as optional depending on the
// typings of the component.
export function c<TProps>(...[Provider, props]: cProps<TProps>) {
  // The casting is necessary to group the differents providers in the same resulting array.
  // At this point, we don't care about the exact typing of the providers since the check is done
  // on `cProps`.
  return [Provider, props ?? {}] as [
    React.ComponentType<UnknownObject>,
    UnknownObject,
  ];
}
